export enum Currency {
  SAR = 'SAR',
}

export type Language = 'AR' | 'EN';

export enum EventTypes {
  AFTER_PAGE_VIEW = 'after_page_view',
  MOVIE_FINDER = 'movie_finder',
  LOGIN_SUCCESS = 'login_success',
  CTA_BUTTON_CLICK = 'cta_button_click',
  CONTACT_FORM_SUCCESS = 'contact_form_success',
  SELECT_OFFER = 'select_offer',
  VIEW_ITEM_LIST = 'view_item_list',
  SELECT_ITEM = 'select_item',
  VIEW_ITEM = 'view_item',
  ADD_TO_CART = 'add_to_cart',
  BEGIN_CHECKOUT = 'begin_checkout',
  VOUCHER_ENTRY = 'voucher_entry',
  PURCHASE = 'purchase',
  PAYMENT_FAILURE = 'payment_failure',
  CONTINUE_AS_GUEST = 'continue_as_guest',
  MY_BOOKINGS = 'my_booking',
  PRIVATE_BOOKING = 'private_booking',
  COMING_SOON_MOVIE = 'coming_soon_movie',
  SHOW_BRAZE_NOTIFICATIONS = 'show_braze_notifications',
}

export type DataLayerEventsModels =
  | AfterPageViewModel
  | MovieFinderModel
  | LoginSuccessModel
  | CTAButtonClickModel
  | ContactFormSuccessModel
  | SelectOfferModel
  | ViewItemListModel
  | SelectItemModel
  | ViewItemModel
  | AddToCartModel
  | BeginCheckoutModel
  | VoucherEntryModel
  | PurchaseModel
  | PaymentFailureModel
  | ContinueAsGuestModel
  | MyBookingsModel
  | PrivateBookingModel
  | ComingSoonMovieModel
  | ShowBrazeNotificationsModel;

export interface AfterPageViewModel {
  event: EventTypes.AFTER_PAGE_VIEW;
  page_section: string; // Page Section : eg: Home/ Experience / Cinamas etc
  page_title: string; // Title of the page
  page_url: string;
  language: Language; // page language
  location: string; // Selected location eg: Jeddah
}

export interface MovieFinderModel {
  event: EventTypes.MOVIE_FINDER;
  search_text: string; //Search text (movie name)
  page_title: string; // Title of the page
  page_url: string;
  location: string; //selected location
  language: 'AR' | 'EN'; // selected language
}

export interface LoginSuccessModel {
  event: EventTypes.LOGIN_SUCCESS;
  page_section: string; // Page Section : eg: Home/ Experience / Cinamas etc
  page_title: string; // Title of the page
  page_url: string;
}

export interface CTAButtonClickModel {
  event: EventTypes.CTA_BUTTON_CLICK;
  button_click_text: string; // Text on the button eg: "Explore"
  button_click_url: string; // Destination URL
  block_section: string; // Section of the block which contain the clicked button eg: Cinema
  block_title: string; // Title of the block which contain the clicked button eg: Atelier La Vie
  page_section: string; // Page Section : eg: Home/ Experience / Cinamas etc
  page_title: string; // Title of the page
  page_url: string;
}

export interface ContactFormSuccessModel {
  event: EventTypes.CONTACT_FORM_SUCCESS;
  lead_type: string; // Type of the lead eg: enquiry, complaint
  mall: string; // Selected mall
  location: string; // selected location
  page_section: string; // Page Section : eg: Home/ Experience / Cinamas etc
  page_title: string; // Title of the page
  page_url: string;
}

export interface SelectOfferModel {
  event: EventTypes.SELECT_OFFER;
  offer_title: string; // Title of the selected offer
  page_section: string; // Page Section : eg: Home/ Experience / Cinamas etc
  page_title: string; // Title of the page
  page_url: string;
  language: 'AR' | 'EN';
  location: string; // selected location
}

export interface MovieItemModel {
  item_id: string; // unique id of the movie
  item_name: string; // Movie Name
  item_brand: string; // movie Genre
  item_category: string; // Movie Category
  item_category2: string; // any other movie variant
  item_list_id: string;
  item_list_name: string;
  location: string; // selected location eg: Jeddah
  mall: string; //Mall name eg: Aziz Mall
  price: number; // price of ticket if can be added in this stage
  quantity: number;
  movie_language?: string;
  experience?: string; //eg: ScreenX/Xperience/Standard etc
  selected_time?: string; // 9 AM
  voucher_name?: string; // Name of the voucher eg: Summer fun
  voucher_value?: string;
}

export interface ViewItemListModel {
  event: EventTypes.VIEW_ITEM_LIST;
  ecommerce: {
    item_list_name: string; // the category of listed movies eg: search
    items: MovieItemModel[];
  };
}

export interface SelectItemModel {
  event: EventTypes.SELECT_ITEM;
  ecommerce: {
    item_list_id: string; // Item list id if any
    item_list_name: string; // Item list name eg: Search result
    items: [MovieItemModel];
  };
}

export interface ViewItemModel {
  event: EventTypes.VIEW_ITEM;
  ecommerce: {
    currency: Currency.SAR;
    value: 0; // If there is any value
    items: [MovieItemModel];
  };
}
export interface ComingSoonMovieModel {
  event: EventTypes.COMING_SOON_MOVIE;
  ecommerce: {
    currency: Currency.SAR;
    value: 0; // If there is any value
    items: [MovieItemModel];
  };
}

export interface AddToCartModel {
  event: EventTypes.ADD_TO_CART;
  ecommerce: {
    currency: Currency.SAR;
    value: number;
    items: [MovieItemModel];
  };
}

export interface BeginCheckoutModel {
  event: EventTypes.BEGIN_CHECKOUT;
  ecommerce: {
    currency: Currency.SAR;
    value: number;
    coupon: string; // Add if any coupon applied
    items: [MovieItemModel];
  };
}

export interface VoucherEntryModel {
  event: EventTypes.VOUCHER_ENTRY;
  ecommerce: {
    currency: Currency.SAR;
    value: number;
    coupon: string; // Add if any coupon applied
    items: [MovieItemModel];
  };
}

export interface PurchaseModel {
  event: EventTypes.PURCHASE;
  ecommerce: {
    transaction_id: string; // Transaction ID
    value: number; // Sum of (price * quantity) for all items.
    tax: number; //Tax if applicable
    currency: Currency.SAR;
    coupon: string; // If any voucher applied
    items: [MovieItemModel];
  };
}

export interface PaymentFailureModel {
  event: EventTypes.PAYMENT_FAILURE;
  error_text: string; // Payment failure reason text
  error_code: string | number; // Payment failure reason code
  page_section: string; // Page Section : eg: Payment etc
  page_title: string; // Title of the page
  page_url: string;
  location: string; // selected location
}

export interface ContinueAsGuestModel {
  event: EventTypes.CONTINUE_AS_GUEST;
  movie_name: string; //selected movie
  location: string; //selected location
  mall: string; // selected mall
  page_section: string; // Page Section : eg: checkout etc
  page_title: string; // Title of the page
  page_url: string;
  language: 'AR' | 'EN'; // selected website language
}

export interface MyBookingsModel {
  event: EventTypes.MY_BOOKINGS;
}

export interface ShowBrazeNotificationsModel {
  event: EventTypes.SHOW_BRAZE_NOTIFICATIONS;
}
export interface PrivateBookingModel {
  event: EventTypes.PRIVATE_BOOKING;
  customer_name: string;
  email: string;
  phone_number: string;
  location: string;
  cinema_name: string;
  experience_name: string;
  film_name: string;
  selected_time: string;
  number_of_guests: number;
}
